import type {ReactElement} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {BaseDownloadDuration} from '@Components/download-panel/components/base-download-duration/base-download-duration';
import {noop} from '@Utils/general.util';
import {DOWNLOAD_MAX_DURATION} from '@Libraries/poster-download-library';

interface DownloadVideoDurationOptionProps {
  onSlideCallback?(): void;
  onSlideEndedCallback?(): void;
}

const DOWNLOAD_MIN_DURATION = 2;

export function DownloadVideoDurationOption({onSlideCallback = noop, onSlideEndedCallback = noop}: DownloadVideoDurationOptionProps): ReactElement {
  const {duration, setDuration, originalDuration} = useDownloadPanelData();
  const cappedDuration = Math.min(duration, DOWNLOAD_MAX_DURATION);
  const cappedOriginalDuration = Math.min(originalDuration, DOWNLOAD_MAX_DURATION);

  const onDurationChanged = (value: number): void => {
    setDuration(Math.min(value, DOWNLOAD_MAX_DURATION));
  };

  const onSlide = (value: number): void => {
    onSlideCallback();
    setDuration(Math.min(getExponentialFunctionBackwards(value), DOWNLOAD_MAX_DURATION));
  };

  const VALUE_BREAKPOINT = 60;
  const SLIDER_BREAKPOINT_PERCENTAGE = 0.9;

  const VALUE_RANGE_FIRST_PART = VALUE_BREAKPOINT - DOWNLOAD_MIN_DURATION;
  const VALUE_RANGE_SECOND_PART = DOWNLOAD_MAX_DURATION - VALUE_BREAKPOINT;
  const SLIDER_TRACK_RANGE = DOWNLOAD_MAX_DURATION - DOWNLOAD_MIN_DURATION;
  const SLIDER_BREAKPOINT = DOWNLOAD_MIN_DURATION + SLIDER_TRACK_RANGE * SLIDER_BREAKPOINT_PERCENTAGE;
  const SLIDER_RANGE_FIRST_PART = SLIDER_BREAKPOINT - DOWNLOAD_MIN_DURATION;
  const SLIDER_RANGE_SECOND_PART = DOWNLOAD_MAX_DURATION - SLIDER_BREAKPOINT;

  const getExponentialFunctionForwards = (val: number): number => {
    const clampedVal = Math.max(DOWNLOAD_MIN_DURATION, Math.min(val, DOWNLOAD_MAX_DURATION));

    if (clampedVal <= VALUE_BREAKPOINT) {
      const percentage = (clampedVal - DOWNLOAD_MIN_DURATION) / VALUE_RANGE_FIRST_PART;
      return DOWNLOAD_MIN_DURATION + percentage * SLIDER_RANGE_FIRST_PART;
    } else {
      const percentage = (clampedVal - VALUE_BREAKPOINT) / VALUE_RANGE_SECOND_PART;
      return SLIDER_BREAKPOINT + percentage * SLIDER_RANGE_SECOND_PART;
    }
  };

  const getExponentialFunctionBackwards = (sliderPos: number): number => {
    const clampedSliderPos = Math.max(DOWNLOAD_MIN_DURATION, Math.min(sliderPos, DOWNLOAD_MAX_DURATION));

    if (clampedSliderPos <= SLIDER_BREAKPOINT) {
      const percentage = (clampedSliderPos - DOWNLOAD_MIN_DURATION) / SLIDER_RANGE_FIRST_PART;
      const val = DOWNLOAD_MIN_DURATION + percentage * VALUE_RANGE_FIRST_PART;
      return val;
    } else {
      const percentage = (clampedSliderPos - SLIDER_BREAKPOINT) / SLIDER_RANGE_SECOND_PART;
      const val = VALUE_BREAKPOINT + percentage * VALUE_RANGE_SECOND_PART;
      return val;
    }
  };

  return (
    <BaseDownloadDuration
      marks={[getExponentialFunctionForwards(cappedOriginalDuration)]}
      duration={cappedDuration}
      onDurationChanged={onDurationChanged}
      originalDuration={cappedOriginalDuration}
      sliderValue={getExponentialFunctionForwards(cappedDuration)}
      onSlide={onSlide}
      min={DOWNLOAD_MIN_DURATION}
      max={DOWNLOAD_MAX_DURATION}
      onSlideEndedCallback={onSlideEndedCallback}
    />
  );
}
